import { PaymentMethodEnum } from '.spec/self-care/tsTypes';
import { sortBy } from 'lodash';

import type { Amount } from '@skippay/core/@types/amount';
import { PartyAccount } from '@skippay/core/@types/bank';
import type { Country } from '@skippay/core/@types/country';
import { toFloat } from '@skippay/core/utils/amountUtils';
import { isValidIBANInPartyAccount } from '@skippay/core/utils/iban';
import type { Maybe } from '@skippay/core/utils/types';

import MyPaymentCreateMutation from 'mutations/MyPaymentCreateMutation.graphql';
import { getBankAccount } from 'sections/payment/bankTransfer/BankAccount';

import {
	type BillingInfo,
	type CustomerContext,
	type DeferredPaymentItem,
	PaymentDebtType,
	type PaymentInstructions,
} from '../../types';
import type { MutationSource } from '../types';

const RECIPIENT_NAME = 'Skip Pay';
const NO_DUE_DATE = '9999-12-31';

export type PaymentCreateData = {
	myPaymentCreate?: Maybe<{
		additionalInfo?: Maybe<{
			variableSymbol?: Maybe<string>;
			referenceNumber?: Maybe<string>;
		}>;
		partyAccount?: Maybe<Partial<PartyAccount>>;
		value?: Maybe<Amount>;
	}>;
};

export type PaymentTarget = {
	debtType: PaymentDebtType;
	id: string;
	value: Amount;
	country: Country;
};

const isPayableBilling = (billing?: BillingInfo): billing is BillingInfo =>
	!!billing?.payable && billing.canPay === true && toFloat(billing.payable.value) > 0;

const isPayableDeferredPayment = (item: DeferredPaymentItem): boolean =>
	!!item.payable && item.canPay !== false && toFloat(item.payable.value) > 0;

const mostUrgentDeferredPayment = (
	ctx: Readonly<CustomerContext>
): DeferredPaymentItem | undefined =>
	sortBy(
		(ctx.deferredPayments ?? []).filter(isPayableDeferredPayment),
		(item) => item.dueDate ?? NO_DUE_DATE
	)[0];

interface ResolvedAccount {
	value: string;
	isIban: boolean;
}

const toAccount = (
	account: Partial<PartyAccount>,
	country: Country
): ResolvedAccount | undefined => {
	const { accountNumber, bankCode, iban } = account;

	if (
		country !== 'CZ' &&
		iban &&
		isValidIBANInPartyAccount({ accountNumber: '', bankCode: '', iban })
	) {
		return { value: iban, isIban: true };
	}

	if (accountNumber && bankCode) {
		return { value: getBankAccount({ ...account, accountNumber, bankCode }), isIban: false };
	}

	return iban ? { value: iban, isIban: true } : undefined;
};

export const paymentInstructionsSource: MutationSource<PaymentCreateData, PaymentTarget> = {
	kind: 'mutation',
	domains: ['paymentInstructions'],
	mutation: MyPaymentCreateMutation,

	prepare: (ctx, { country }) => {
		const billing = [ctx.unpaidBilling, ctx.billing].find(isPayableBilling);
		if (billing?.payable) {
			return { debtType: PaymentDebtType.BILLING, ...billing.payable, country };
		}
		const deferredPayment = mostUrgentDeferredPayment(ctx);
		if (deferredPayment?.payable) {
			return { debtType: PaymentDebtType.DEFERRED_PAYMENT, ...deferredPayment.payable, country };
		}
		return undefined;
	},

	getVariables: (target) => ({
		body: {
			method: PaymentMethodEnum.TRANSFER,
			...(target.debtType === PaymentDebtType.BILLING
				? { billingIds: [target.id] }
				: { applicationIds: [target.id] }),
			value: target.value,
		},
	}),

	toModel: (data, target) => {
		const order = data.myPaymentCreate;
		if (!order?.partyAccount) return {};
		const account = toAccount(order.partyAccount, target.country);
		if (!account) return {};

		const { additionalInfo } = order;
		const instructions: PaymentInstructions = {
			account: account.value,
			recipientName: account.isIban ? RECIPIENT_NAME : undefined,
			variableSymbol: account.isIban ? undefined : (additionalInfo?.variableSymbol ?? undefined),
			referenceNumber: account.isIban ? (additionalInfo?.referenceNumber ?? undefined) : undefined,
			amount: toFloat(order.value ?? target.value),
			debtType: target.debtType,
		};
		return { paymentInstructions: instructions };
	},
};
